package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.hukakmp.data.FishingRepository
import com.example.hukakmp.model.FirebaseResult
import com.example.hukakmp.model.PartePesca
import dev.gitlive.firebase.firestore.toMilliseconds
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn

/**
 * Convierte "yyyy-MM-dd" o "dd/MM/yyyy" a epoch millis para poder ordenar.
 * Portado desde Juka/.../viewmodel/ReportesViewModel.kt, reemplazando
 * java.text.SimpleDateFormat (no disponible en iOS) por kotlinx-datetime.
 */
private fun fechaAEpoch(fecha: String?): Long {
    if (fecha.isNullOrBlank()) return 0L
    val partesIso = fecha.split("-")
    if (partesIso.size == 3) {
        toEpochMillisOrNull(partesIso[0], partesIso[1], partesIso[2])?.let { return it }
    }
    val partesDmy = fecha.split("/")
    if (partesDmy.size == 3) {
        toEpochMillisOrNull(partesDmy[2], partesDmy[1], partesDmy[0])?.let { return it }
    }
    return 0L
}

private fun toEpochMillisOrNull(anio: String, mes: String, dia: String): Long? = try {
    LocalDate(anio.toInt(), mes.toInt(), dia.toInt())
        .atStartOfDayIn(TimeZone.currentSystemDefault())
        .toEpochMilliseconds()
} catch (e: Exception) {
    null
}

class ReportesViewModel(
    private val repository: FishingRepository,
) : ViewModel() {

    data class ReportesUiState(
        val reportes: List<PartePesca> = emptyList(),
        val isLoading: Boolean = false,
        val error: String? = null,
    )

    private val _uiState = MutableStateFlow(ReportesUiState())
    val uiState: StateFlow<ReportesUiState> = _uiState.asStateFlow()

    init {
        cargarReportes()
    }

    fun cargarReportes() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            try {
                val lista = repository.obtenerMisPartes()
                val ordenados = lista.sortedByDescending { r -> fechaAEpoch(r.fecha) }
                _uiState.update { it.copy(isLoading = false, reportes = ordenados) }
            } catch (e: Exception) {
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }

    /** Ventana de tiempo durante la que se puede borrar un parte recién creado. */
    private val limiteEliminacionMs = 60L * 60L * 1000L // 1 hora

    /** true si el reporte todavía está dentro de la hora de haberse creado. */
    fun puedeEliminarse(reporte: PartePesca): Boolean {
        val creadoEn = reporte.timestamp?.toMilliseconds()?.toLong() ?: return false
        return kotlin.time.Clock.System.now().toEpochMilliseconds() - creadoEn <= limiteEliminacionMs
    }

    fun eliminarParte(id: String) {
        viewModelScope.launch {
            val actuales = _uiState.value.reportes
            val aEliminar = actuales.firstOrNull { it.id == id }
            _uiState.update { it.copy(reportes = actuales.filterNot { r -> r.id == id }, error = null) }

            val resultado = repository.eliminarParte(id)
            if (resultado is FirebaseResult.Error) {
                if (aEliminar != null) {
                    _uiState.update {
                        it.copy(
                            reportes = (it.reportes + aEliminar).sortedByDescending { r -> fechaAEpoch(r.fecha) },
                            error = "No se pudo eliminar el reporte. Probá de nuevo.",
                        )
                    }
                }
            }
        }
    }
}
